import styled, { css } from 'styled-components';

export const Section = styled.section`
  ${({ theme }) => css`
    width: 100%;
    padding: 3rem 0;
    background-color: ${theme.backgroundColors.light};

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      padding: 2rem 0;
    }
  `}
`;

/* Outer wrapper — position:relative so DesktopImage (position:absolute) anchors here.
   overflow:visible so the image can bleed above the card top on desktop. */
export const CardClip = styled.div`
  ${({ theme }) => css`
    position: relative;
    overflow: visible;
    display: flex;
    flex-direction: column;
    border-radius: ${theme.borderRadius.large};
  `}
`;

export const Card = styled.div<{ backgroundImage?: string }>`
  ${({ theme, backgroundImage }) => css`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: stretch;
    overflow: hidden;
    border-radius: ${theme.borderRadius.large};
    background-color: ${theme.colors.dark};
    min-height: 20rem;
    flex: 1;

    ${backgroundImage
      ? css`
          background-image: url('${backgroundImage}');
          background-size: cover;
          background-position: center center;
          background-repeat: no-repeat;
        `
      : ''}

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      flex-direction: column;
      min-height: auto;
      /* Keep background image on mobile — shift focus to left */
      ${backgroundImage
        ? css`
            background-position: left center;
          `
        : ''}
    }
  `}
`;

export const ContentSide = styled.div`
  ${({ theme }) => css`
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 1.5rem;
    padding: 3rem 3rem 3rem 3.5rem;
    width: 50%;
    flex-shrink: 0;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 2.5rem 2rem 2.5rem 2.5rem;
      width: 55%;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      width: 100%;
      padding: 2.5rem 1.5rem 1.5rem;
    }
  `}
`;

export const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const Actions = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    align-items: center;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      align-items: flex-start;
    }
  `}
`;

/* Primary CTA button — green pill, black text, matching Figma exactly */
export const PrimaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;

  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1;
  letter-spacing: -0.15px;
  color: #000000;

  border-radius: 9999px;
  padding: 0.875rem 1.75rem;

  background-color: #8cc63f;
  border: 2px solid #8cc63f;

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    transform 0.125s ease-in-out;

  &:hover {
    background-color: transparent;
    color: #8cc63f;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.975);
  }
`;

/* Secondary CTA button — transparent pill, white border + text */
export const SecondaryButton = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  cursor: pointer;
  text-decoration: none;

  font-family: 'Inter', sans-serif;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1;
  letter-spacing: -0.15px;
  color: #ffffff;

  border-radius: 9999px;
  padding: 0.875rem 1.75rem;

  background-color: transparent;
  border: 1px solid #ffffff;

  transition: background-color 0.2s ease-in-out, color 0.2s ease-in-out,
    transform 0.125s ease-in-out;

  &:hover {
    background-color: #ffffff;
    color: #1a2744;
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.975);
  }
`;

/* Spacer inside Card — reserves right column space on desktop */
export const ImageSpacer = styled.div`
  ${({ theme }) => css`
    flex: 1;
    min-width: 0;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  `}
`;

/* ─────────────────────────────────────────────────────────────
   DESKTOP image — position:absolute relative to CardClip.
   Anchored bottom-right, height 115% bleeds above card top.
   Hidden on mobile (MobileImage takes over).
────────────────────────────────────────────────────────────── */
export const DesktopImage = styled.img`
  ${({ theme }) => css`
    position: absolute;
    bottom: 0;
    right: 0;
    z-index: 10;
    height: 115%;
    width: auto;
    max-width: none;
    object-fit: contain;
    object-position: bottom right;
    pointer-events: none;
    display: block;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      height: 108%;
    }

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      display: none;
    }
  `}
`;

/* ─────────────────────────────────────────────────────────────
   MOBILE image — in-flow block inside Card's flex-column.
   Shows below ContentSide on mobile — full image, no crop.
   Hidden on desktop (DesktopImage takes over).
────────────────────────────────────────────────────────────── */
export const MobileImage = styled.img`
  ${({ theme }) => css`
    display: none;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      display: block;
      /* Let the image keep its natural aspect ratio — no cropping */
      width: 100%;
      height: auto;
      max-height: none;
      object-fit: contain;
      object-position: center bottom;
      flex-shrink: 0;
      /* Align image to the right so person stays visible on narrow screens */
      margin-left: auto;
      /* Cap width so image doesn't stretch too wide on tablet */
      max-width: 400px;
      align-self: flex-end;
    }
  `}
`;
